import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useBooking } from '../../hooks/useBooking';
import AuthService from '../../services/AuthService';
import { PAGES } from '../../utils/constants';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const AuthCallbackPage = () => {
  const { authSuccess } = useAuth();
  const { setCurrentPage } = useBooking();
  const [error, setError] = useState(null);
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    // If already processing or finished, don't run again
    if (processed) return;

    const handleCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');

        if (!accessToken || !refreshToken) {
          throw new Error('Authentication tokens missing in URL');
        }

        setProcessed(true);

        // Fetch user profile using the access token
        const user = await AuthService.getCurrentUser(accessToken);

        if (!user) {
          throw new Error('Failed to fetch user profile');
        }

        // Finalize login in AuthContext
        authSuccess(user, accessToken, refreshToken);

        // Immediately clear URL tokens using replaceState to prevent loops
        window.history.replaceState({}, document.title, window.location.pathname);

        // Redirect to homepage after a short delay to show success UI
        setTimeout(() => {
          setCurrentPage(PAGES.HOME);
          // Ensure we are fully on the homepage URL
          window.history.pushState({}, '', '/');
        }, 1500);

      } catch (err) {
        console.error('Auth Callback Error:', err);
        setError(err.message || 'An error occurred during sign-in.');
        
        // Redirect to home after error too so user isn't stuck
        setTimeout(() => {
          setCurrentPage(PAGES.HOME);
          window.history.replaceState({}, '', '/');
        }, 3000);
      }
    };

    handleCallback();
  }, [authSuccess, setCurrentPage, processed]);

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-xl shadow-2xl p-10 text-center transform transition-all duration-500 hover:scale-105">
          {!error ? (
            <div className="space-y-6">
              <div className="flex justify-center">
                <LoadingSpinner size="large" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Finalizing Sign-In</h2>
              <p className="text-gray-500 font-medium">Capturing your session and preparing your dashboard...</p>
              <div className="w-full bg-gray-100 h-1 rounded-full overflow-hidden mt-4">
                <div className="bg-blue-600 h-full animate-[loading_1.5s_ease-in-out_infinite] origin-left"></div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-red-500 text-6xl mb-4 animate-bounce">⚠️</div>
              <h2 className="text-2xl font-bold text-gray-900">Sign-In Failed</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <p className="text-sm text-gray-400">Redirecting you to the home page...</p>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes loading {
          0% { transform: scaleX(0); }
          50% { transform: scaleX(0.7); }
          100% { transform: scaleX(1); }
        }
      `}</style>
    </Layout>
  );
};

export default AuthCallbackPage;
